import { HISTORY } from "@/config/designer";

export interface HistoryCommand {
  readonly label: string;
  /** Retained memory, bytes — used to trim the stack. */
  readonly bytes: number;
  undo(): void;
  redo(): void;
}

/**
 * Undo/redo as a command stack rather than whole-field snapshots.
 *
 * Sculpting commands keep only the voxel blocks a stroke actually touched, and
 * redo replays the recorded dabs rather than storing a second copy — so a long
 * session of strokes costs roughly one stroke's footprint each. Older entries
 * are dropped once the retained total passes the configured budget, which
 * bounds memory without ever bounding what the user can do.
 */
export class HistoryStack {
  private undoStack: HistoryCommand[] = [];
  private redoStack: HistoryCommand[] = [];
  private listeners = new Set<() => void>();

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
  get depth(): number {
    return this.undoStack.length;
  }
  get retainedBytes(): number {
    let total = 0;
    for (const c of this.undoStack) total += c.bytes;
    for (const c of this.redoStack) total += c.bytes;
    return total;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  push(command: HistoryCommand): void {
    this.undoStack.push(command);
    this.redoStack = [];
    this.trim();
    this.emit();
  }

  undo(): HistoryCommand | null {
    const command = this.undoStack.pop();
    if (!command) return null;
    command.undo();
    this.redoStack.push(command);
    this.emit();
    return command;
  }

  redo(): HistoryCommand | null {
    const command = this.redoStack.pop();
    if (!command) return null;
    command.redo();
    this.undoStack.push(command);
    this.emit();
    return command;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.emit();
  }

  private trim(): void {
    while (this.undoStack.length > HISTORY.maxEntries) this.undoStack.shift();
    while (this.retainedBytes > HISTORY.maxBytes && this.undoStack.length > 1) {
      this.undoStack.shift();
    }
  }
}
